import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/axiosConfig';
import './AdminTramites.css';

function BuscadorUsuario({ usuarios, value, onChange, label, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = usuarios.find(u => u.id === Number(value));

  const filtered = query.trim()
    ? usuarios.filter(u => {
        const q = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const name = (u.nombre || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const cuit = (u.cuit || '').toLowerCase();
        const rol = (u.rol || '').toLowerCase();
        return name.includes(q) || cuit.includes(q) || rol.includes(q);
      })
    : usuarios;

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (user) => {
    onChange(String(user.id));
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  if (selected && !open) {
    return (
      <div className="buscador-usuario">
        {label && <label>{label}</label>}
        <div className="buscador-seleccionado">
          <i className="bi bi-person-check-fill" style={{ color: 'var(--primary)', fontSize: '18px' }}></i>
          <div className="buscador-seleccionado-info">
            <span className="buscador-seleccionado-nombre">{selected.nombre}</span>
            <span className="buscador-seleccionado-meta">
              {selected.cuit ? `${selected.cuit} - ` : ''}{selected.rol}
            </span>
          </div>
          <button
            className="buscador-seleccionado-cambiar"
            onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            type="button"
          >
            Cambiar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="buscador-usuario" ref={wrapRef}>
      {label && <label>{label}</label>}
      <div className="buscador-input-wrap">
        <i className="bi bi-search buscador-icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder="Escribe para buscar..."
          value={query}
          autoFocus
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlightIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {open && (
        <div className="buscador-dropdown">
          {filtered.length === 0 ? (
            <div className="buscador-no-results">Sin resultados</div>
          ) : (
            filtered.map((u, i) => (
              <div
                key={u.id}
                className={`buscador-option ${i === highlightIdx ? 'highlighted' : ''}`}
                onClick={() => handleSelect(u)}
                onMouseEnter={() => setHighlightIdx(i)}
              >
                <span className="buscador-option-name">{u.nombre}</span>
                <span className="buscador-option-meta">
                  {u.cuit ? `${u.cuit} - ` : ''}{u.rol}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const AdminTramites = ({ addToast, targetEntidad, onNavigate }) => {
  const [activeTab, setActiveTab] = useState(targetEntidad ? 'activos' : 'plantillas');

  const [plantillas, setPlantillas] = useState([]);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [plantillaPasos, setPlantillaPasos] = useState([]);

  const [usuarios, setUsuarios] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [selectedEntidadId, setSelectedEntidadId] = useState('');

  const [entidadesActivas, setEntidadesActivas] = useState([]);
  const [finalizados, setFinalizados] = useState([]);
  const [loadingFinalizados, setLoadingFinalizados] = useState(false);
  const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
  const [tramitesEntidad, setTramitesEntidad] = useState([]);
  const [loadingEntidades, setLoadingEntidades] = useState(false);
  const [loadingTramites, setLoadingTramites] = useState(false);

  const [showCrearModal, setShowCrearModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [editarPlantillaId, setEditarPlantillaId] = useState(null);
  const [formNombre, setFormNombre] = useState('');
  const [formDescripcion, setFormDescripcion] = useState('');
  const [formPasos, setFormPasos] = useState([{ orden: 1, titulo_paso: '', descripcion_paso: '' }]);
  const [formCategoriaId, setFormCategoriaId] = useState('');
  const [categorias, setCategorias] = useState([]);

  const [entidadSearch, setEntidadSearch] = useState('');
  const [entidadSearchOpen, setEntidadSearchOpen] = useState(false);

  const [undoData, setUndoData] = useState(null);
  const undoTimeoutRef = useRef(null);
  const undoIntervalRef = useRef(null);
  const pasosRef = useRef(null);
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [catModalNombre, setCatModalNombre] = useState('');

  const fetchPlantillas = useCallback(async () => {
    try {
      const res = await api.post('/obtenerTramites');
      setPlantillas(res.data || []);
    } catch (err) {
      console.error('Error al obtener plantillas:', err);
    }
  }, []);

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await api.post('/obtenerUsuarios');
      setUsuarios(res.data || []);
    } catch (err) {
      console.error('Error al obtener usuarios:', err);
    }
  }, []);

  const fetchTodasEntidades = useCallback(async () => {
    try {
      const res = await api.get('/obtenerTodasLasEntidades');
      setEntidadesActivas(res.data?.data || res.data || []);
    } catch (err) {
      console.error('Error al obtener entidades:', err);
      setEntidadesActivas([]);
    }
  }, []);

  const fetchEntidades = useCallback(async () => {
    setLoadingEntidades(true);
    setEntidadSeleccionada(null);
    setTramitesEntidad([]);
    try {
      const res = await api.post('/obtenerEntidadesConTramites');
      setEntidades(res.data || []);
    } catch (err) {
      console.error('Error al obtener entidades:', err);
      setEntidades([]);
    } finally {
      setLoadingEntidades(false);
    }
  }, []);

  const fetchFinalizados = useCallback(async () => {
    setLoadingFinalizados(true);
    try {
      const res = await api.post('/obtenerTramitesCompletados');
      setFinalizados(res.data || []);
    } catch (err) {
      console.error('Error al obtener finalizados:', err);
      setFinalizados([]);
    } finally {
      setLoadingFinalizados(false);
    }
  }, []);

  const fetchTramitesEntidad = useCallback(async (entidadId) => {
    setLoadingTramites(true);
    try {
      const res = await api.post('/obtenerTramitesPorEntidad', { entidadId });
      setTramitesEntidad(res.data || []);
    } catch (err) {
      console.error('Error al obtener trámites:', err);
      setTramitesEntidad([]);
    } finally {
      setLoadingTramites(false);
    }
  }, []);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await api.post('/obtenerCategorias');
      setCategorias(res.data || []);
    } catch (err) {
      console.error('Error al obtener categorías:', err);
      setCategorias([]);
    }
  }, []);

  useEffect(() => {
    fetchPlantillas();
    fetchUsuarios();
    fetchTodasEntidades();
    fetchCategorias();
  }, [fetchPlantillas, fetchUsuarios, fetchTodasEntidades, fetchCategorias]);

  useEffect(() => {
    if (targetEntidad) {
      setActiveTab('activos');
      setEntidadSeleccionada(targetEntidad);
      fetchTramitesEntidad(targetEntidad);
      fetchEntidades();
    }
  }, [targetEntidad, fetchTramitesEntidad, fetchEntidades]);

  const handleVerPasos = async (plantillaId) => {
    try {
      const res = await api.post('/obtenerPasosPlantilla', { plantillaId });
      setPlantillaPasos(res.data || []);
      setSelectedPlantilla(plantillaId);
      setTimeout(() => {
        if (pasosRef.current) pasosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (err) {
      console.error('Error al obtener pasos:', err);
    }
  };

  const handleCrearPlantilla = async (e) => {
    e.preventDefault();
    if (!formNombre || !formDescripcion) {
      addToast('Completa el nombre y la descripcion', 'error');
      return;
    }
    const pasosValidos = formPasos.filter(p => p.titulo_paso.trim());
    if (pasosValidos.length === 0) {
      addToast('Agrega al menos un paso', 'error');
      return;
    }
    try {
      const pasos = pasosValidos.map((p, i) => ({
        orden: i + 1,
        titulo_paso: p.titulo_paso,
        descripcion_paso: p.descripcion_paso
      }));
      await api.post('/crearTramite', {
        nombre: formNombre,
        descripcion: formDescripcion,
        pasos,
        categoriaId: formCategoriaId ? Number(formCategoriaId) : null
      });
      addToast('Plantilla creada correctamente');
      setShowCrearModal(false);
      setFormNombre('');
      setFormDescripcion('');
      setFormCategoriaId('');
      setFormPasos([{ orden: 1, titulo_paso: '', descripcion_paso: '' }]);
      fetchPlantillas();
    } catch (err) {
      console.error('Error al crear plantilla:', err);
      addToast('Error al crear la plantilla', 'error');
    }
  };

  const handleEliminarPlantilla = async (plantillaId) => {
    if (!window.confirm('¿Estas seguro de eliminar esta plantilla?')) return;
    try {
      await api.post('/eliminarPlantilla', { plantillaId });
      addToast('Plantilla eliminada correctamente');
      setSelectedPlantilla(null);
      setPlantillaPasos([]);
      fetchPlantillas();
    } catch (err) {
      console.error('Error al eliminar plantilla:', err);
      addToast('Error al eliminar la plantilla', 'error');
    }
  };

  const handleToggleInicioUsuario = async (plantillaId, e) => {
    e.stopPropagation();
    try {
      const res = await api.post('/toggleInicioUsuario', { plantillaId });
      setPlantillas(prev => prev.map(p => p.id === plantillaId ? { ...p, inicio_usuario: res.data.inicio_usuario ? 1 : 0 } : p));
      addToast(res.data.inicio_usuario ? 'Usuarios pueden iniciar este trámite' : 'Solo admin puede iniciar este trámite');
    } catch (err) {
      console.error('Error al togglear inicio usuario:', err);
      addToast('Error al cambiar permiso', 'error');
    }
  };

  const handleAbrirEditar = async (plantilla) => {
    setEditarPlantillaId(plantilla.id);
    setFormNombre(plantilla.nombre);
    setFormDescripcion(plantilla.descripcion);
    setFormCategoriaId(String(plantilla.categoria_id || ''));
    try {
      const res = await api.post('/obtenerPasosPlantilla', { plantillaId: plantilla.id });
      const pasos = (res.data || []).map(p => ({
        orden: p.orden,
        titulo_paso: p.titulo_paso,
        descripcion_paso: p.descripcion_paso || ''
      }));
      setFormPasos(pasos.length > 0 ? pasos : [{ orden: 1, titulo_paso: '', descripcion_paso: '' }]);
    } catch (err) {
      console.error('Error al cargar pasos:', err);
      setFormPasos([{ orden: 1, titulo_paso: '', descripcion_paso: '' }]);
    }
    setShowEditarModal(true);
  };

  const handleEditarPlantilla = async (e) => {
    e.preventDefault();
    if (!formNombre || !formDescripcion) {
      addToast('Completa el nombre y la descripcion', 'error');
      return;
    }
    const pasosValidos = formPasos.filter(p => p.titulo_paso.trim());
    if (pasosValidos.length === 0) {
      addToast('Agrega al menos un paso', 'error');
      return;
    }
    try {
      const pasos = pasosValidos.map((p, i) => ({
        orden: i + 1,
        titulo_paso: p.titulo_paso,
        descripcion_paso: p.descripcion_paso
      }));
      await api.post('/editarPlantilla', {
        plantillaId: editarPlantillaId,
        nombre: formNombre,
        descripcion: formDescripcion,
        pasos,
        categoriaId: formCategoriaId ? Number(formCategoriaId) : null
      });
      addToast('Plantilla editada correctamente');
      setShowEditarModal(false);
      setEditarPlantillaId(null);
      if (selectedPlantilla === editarPlantillaId) {
        handleVerPasos(editarPlantillaId);
      } else {
        setSelectedPlantilla(null);
        setPlantillaPasos([]);
      }
      fetchPlantillas();
    } catch (err) {
      console.error('Error al editar plantilla:', err);
      addToast('Error al editar la plantilla', 'error');
    }
  };

  const handleAgregarPaso = () => {
    setFormPasos([...formPasos, { orden: formPasos.length + 1, titulo_paso: '', descripcion_paso: '' }]);
  };

  const handleQuitarPaso = (index) => {
    if (formPasos.length <= 1) return;
    const nuevos = formPasos.filter((_, i) => i !== index).map((p, i) => ({ ...p, orden: i + 1 }));
    setFormPasos(nuevos);
  };

  const handleCambiarPaso = (index, field, value) => {
    const nuevos = [...formPasos];
    nuevos[index][field] = value;
    setFormPasos(nuevos);
  };

  const handleAsignarTramite = async () => {
    if (!selectedPlantilla || !selectedEntidadId) {
      addToast('Selecciona una plantilla y una entidad', 'error');
      return;
    }
    try {
      await api.post('/comenzarTramiteUsuario', {
        plantillaId: selectedPlantilla,
        entidadId: Number(selectedEntidadId)
      });
      addToast('Trámite asignado correctamente');
      setSelectedEntidadId('');
      setSelectedPlantilla(null);
      setActiveTab('activos');
      fetchEntidades();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al asignar el trámite';
      addToast(msg, 'error');
    }
  };

  const refreshTramites = useCallback(() => {
    if (entidadSeleccionada) fetchTramitesEntidad(entidadSeleccionada);
  }, [entidadSeleccionada, fetchTramitesEntidad]);

  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
      if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
    };
  }, []);

  const handleAvanzar = async (seguimientoId) => {
    if (undoData) return;
    try {
      const res = await api.post('/avanzarTramite', { seguimientoId });
      if (res.data.completado) {
        const { usuario, plantillaNombre } = res.data;

        undoTimeoutRef.current = setTimeout(async () => {
          clearInterval(undoIntervalRef.current);
          undoIntervalRef.current = null;
          undoTimeoutRef.current = null;
          setUndoData(null);
          try {
            await api.post('/notificarTramiteCompletado', { seguimientoId });
            addToast(`Trámite terminado. Notificación enviada a ${usuario?.nombre || 'el usuario'}.`);
          } catch {
            addToast('Trámite terminado. Error al enviar la notificación.', 'warning');
          }
          refreshTramites();
        }, 5000);

        let segundos = 5;
        undoIntervalRef.current = setInterval(() => {
          segundos--;
          if (segundos <= 0) clearInterval(undoIntervalRef.current);
          setUndoData(prev => prev ? { ...prev, segundos } : null);
        }, 1000);

        setUndoData({ seguimientoId, usuario, plantillaNombre, segundos: 5 });
      } else {
        addToast('Paso completado correctamente');
        refreshTramites();
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al avanzar el paso';
      addToast(msg, 'error');
    }
  };

  const handleUndoCompletado = () => {
    if (!undoData) return;
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
    undoTimeoutRef.current = null;
    undoIntervalRef.current = null;
    const sid = undoData.seguimientoId;
    setUndoData(null);
    handleRetroceder(sid);
  };

  const handleRetroceder = async (seguimientoId) => {
    try {
      await api.post('/retrocederTramite', { seguimientoId });
      addToast('Paso retrocedido correctamente');
      refreshTramites();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al retroceder el paso';
      addToast(msg, 'error');
    }
  };

  const handleEliminarSeguimiento = async (seguimientoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este trámite? Se borrará todo el historial de pasos.')) return;
    try {
      await api.post('/eliminarSeguimiento', { seguimientoId });
      addToast('Trámite eliminado correctamente');
      if (entidadSeleccionada) {
        fetchTramitesEntidad(entidadSeleccionada);
      } else {
        fetchEntidades();
      }
    } catch (err) {
      addToast('Error al eliminar el trámite', 'error');
    }
  };

  const handleNuevaCategoria = async () => {
    setCatModalNombre('');
    setShowCategoriaModal(true);
  };

  const handleCrearCategoria = async (e) => {
    e.preventDefault();
    if (!catModalNombre.trim()) return;
    try {
      await api.post('/crearCategoria', { nombre: catModalNombre.trim() });
      fetchCategorias();
      setShowCategoriaModal(false);
      addToast('Categoría creada');
    } catch (err) {
      addToast('Error al crear categoría', 'error');
    }
  };

  const handleEliminarCategoria = async (categoriaId) => {
    if (!window.confirm('¿Eliminar esta categoría? Las plantillas quedarán sin categoría.')) return;
    try {
      await api.post('/eliminarCategoria', { categoriaId });
      fetchCategorias();
      fetchPlantillas();
      addToast('Categoría eliminada');
    } catch (err) {
      addToast('Error al eliminar categoría', 'error');
    }
  };

  const handleAsignarCategoria = async (plantillaId, categoriaId) => {
    try {
      await api.post('/asignarCategoriaPlantilla', {
        plantillaId,
        categoriaId: categoriaId ? Number(categoriaId) : null
      });
      fetchPlantillas();
    } catch (err) {
      addToast('Error al cambiar categoría', 'error');
    }
  };

  const calcularProgreso = (pasos) => {
    if (!pasos || pasos.length === 0) return 0;
    const c = pasos.filter(p => p.completado).length;
    return Math.round((c / pasos.length) * 100);
  };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Administración de Trámites</h1>
        <p className="page-subtitle">Gestiona plantillas, asigna y administra trámites activos</p>
      </div>

      <div className="admin-proc-tabs">
        <button className={`admin-proc-tab ${activeTab === 'plantillas' ? 'active' : ''}`} onClick={() => setActiveTab('plantillas')}>
          <i className="bi bi-file-earmark-text"></i> Plantillas
        </button>
        <button className={`admin-proc-tab ${activeTab === 'asignar' ? 'active' : ''}`} onClick={() => setActiveTab('asignar')}>
          <i className="bi bi-person-plus"></i> Asignar
        </button>
        <button className={`admin-proc-tab ${activeTab === 'activos' ? 'active' : ''}`} onClick={() => { setActiveTab('activos'); fetchEntidades(); }}>
          <i className="bi bi-clipboard-check"></i> Trámites Activos
        </button>
        <button className={`admin-proc-tab ${activeTab === 'finalizados' ? 'active' : ''}`} onClick={() => { setActiveTab('finalizados'); fetchFinalizados(); }}>
          <i className="bi bi-check2-all"></i> Finalizados
        </button>
      </div>

      {activeTab === 'plantillas' && renderPlantillas()}
      {activeTab === 'asignar' && renderAsignar()}
      {activeTab === 'activos' && renderActivos()}
      {activeTab === 'finalizados' && renderFinalizados()}

      {showCrearModal && renderModalCrear()}
      {showEditarModal && renderModalEditar()}
      {showCategoriaModal && renderModalCategoria()}

      {undoData && (
        <div className="undo-toast">
          <div className="undo-toast-content">
            <div className="undo-toast-icon">
              <i className="bi bi-check2-circle"></i>
            </div>
            <div className="undo-toast-body">
              <strong>Trámite completado</strong>
              <span>"{undoData.plantillaNombre}" - {undoData.usuario?.nombre || ''}</span>
            </div>
            <div className="undo-toast-timer">
              <svg viewBox="0 0 36 36" className="undo-timer-ring">
                <path className="undo-timer-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path className="undo-timer-fill" strokeDasharray={`${(undoData.segundos / 5) * 100}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <text x="18" y="19" className="undo-timer-text">{undoData.segundos}</text>
              </svg>
            </div>
            <button className="btn btn-undo" onClick={handleUndoCompletado}>
              <i className="bi bi-arrow-counterclockwise"></i> Deshacer
            </button>
          </div>
        </div>
      )}
    </div>
  );

  function renderPlantillas() {
    const grouped = {};
    categorias.forEach(c => grouped[c.id] = { ...c, items: [] });
    grouped['sin'] = { id: null, nombre: 'Sin categoría', orden: 999, items: [] };
    plantillas.forEach(p => {
      const key = p.categoria_id || 'sin';
      if (grouped[key]) grouped[key].items.push(p);
      else grouped['sin'].items.push(p);
    });
    const ordenado = Object.values(grouped).sort((a, b) => a.orden - b.orden);

    return (
      <div className="admin-proc-section">
        <div className="section-header-custom">
          <h3>Plantillas de Trámites</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline" onClick={handleNuevaCategoria}>
              <i className="bi bi-folder-plus"></i> Nueva Categoría
            </button>
            <button className="btn btn-primary" onClick={() => { setFormCategoriaId(''); setFormNombre(''); setFormDescripcion(''); setFormPasos([{ orden: 1, titulo_paso: '', descripcion_paso: '' }]); setShowCrearModal(true); }}>
              <i className="bi bi-plus-lg"></i> Nueva Plantilla
            </button>
          </div>
        </div>

        {ordenado.length === 0 ? (
          <div className="admin-proc-empty"><p>No hay plantillas creadas aun. Crea la primera!</p></div>
        ) : (
          ordenado.map(grupo => (
            <div key={grupo.id || 'sin'} className="categoria-group">
              <div className="categoria-header">
                <i className={`bi ${grupo.id ? 'bi-folder' : 'bi-archive'}`}></i>
                <h4>{grupo.nombre}</h4>
                <span className="categoria-count">{grupo.items.length} plantilla{grupo.items.length !== 1 ? 's' : ''}</span>
                {grupo.id && (
                  <button className="btn-icon-danger-sm" onClick={(e) => { e.stopPropagation(); handleEliminarCategoria(grupo.id); }} title="Eliminar categoría">
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
              <div className="plantillas-grid">
                {grupo.items.length === 0 ? (
                  <p className="categoria-empty">Sin plantillas en esta categoría. Asigná una desde el menú de cada plantilla.</p>
                ) : (
                  grupo.items.map(p => (
                  <div key={p.id} className={`plantilla-card ${selectedPlantilla === p.id ? 'selected' : ''}`} onClick={() => handleVerPasos(p.id)}>
                    <div className="plantilla-card-header">
                      <h4>{p.nombre}</h4>
                      <div className="plantilla-card-header-right">
                        <select className="cat-select-card" value={p.categoria_id || ''} onClick={e => e.stopPropagation()} onChange={e => handleAsignarCategoria(p.id, e.target.value)} title="Cambiar categoría">
                          <option value="">Sin categoría</option>
                          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                        <span className="plantilla-pasos-count">{p.total_pasos} pasos</span>
                      </div>
                    </div>
                    <p className='descripcion-plantilla-card'>{p.descripcion}</p>
                    <div className="plantilla-card-footer">
                      <span className="plantilla-fecha">Creada: {new Date(p.creado_en).toLocaleDateString('es-AR')}</span>
                      <div className="plantilla-card-actions">
                        <label className="toggle-inicio-label" onClick={(e) => e.stopPropagation()} title={p.inicio_usuario ? 'Usuarios pueden iniciar este trámite' : 'Solo admin puede iniciar este trámite'}>
                          <span className="toggle-inicio-text">{p.inicio_usuario ? 'Inicio usuario' : 'Solo admin'}</span>
                          <div className={`toggle-switch ${p.inicio_usuario ? 'active' : ''}`} onClick={(e) => handleToggleInicioUsuario(p.id, e)}>
                            <div className="toggle-knob"></div>
                          </div>
                        </label>
                        <button className="btn-icon-action" onClick={(e) => { e.stopPropagation(); handleAbrirEditar(p); }} title="Editar plantilla">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn-icon-danger" onClick={(e) => { e.stopPropagation(); handleEliminarPlantilla(p.id); }} title="Eliminar plantilla">
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>
          ))
        )}

        {selectedPlantilla && plantillaPasos.length > 0 && (
          <div className="plantilla-pasos-visualizacion" ref={pasosRef}>
            <h4>Pasos de la plantilla seleccionada</h4>
            <div className="proc-pasos-admin">
              {plantillaPasos.map((paso, i) => (
                <div key={paso.id} className="proc-paso">
                  <div className="paso-numero">{i + 1}</div>
                  <div className="paso-contenido">
                    <h4>{paso.titulo_paso}</h4>
                    <p>{paso.descripcion_paso}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderAsignar() {
    const entidadSeleccionada = entidadesActivas.find(e => e.id === Number(selectedEntidadId));
    const entidadesFiltradas = entidadesActivas.filter(e => {
      const q = entidadSearch.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const nombre = ((e.razonSocial || e.nombre || '') + ' ' + (e.cuit || '')).toLowerCase();
      return nombre.includes(q);
    });

    return (
      <div className="admin-proc-section">
        <div className="section-header-custom">
          <h3>Asignar Trámite a Entidad</h3>
        </div>

        <div className="asignar-form">
          <div className="asignar-row">
            <div className="form-group-dash">
              <label>Plantilla</label>
              <select className="form-control" value={selectedPlantilla || ''} onChange={(e) => setSelectedPlantilla(Number(e.target.value))}>
                <option value="">Seleccionar plantilla...</option>
                {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>

            <div className="form-group-dash buscador-entidad-wrap">
              <label>Entidad</label>
              {entidadSeleccionada && !entidadSearchOpen ? (
                <div className="entidad-seleccionada" onClick={() => { setEntidadSearchOpen(true); setEntidadSearch(''); }}>
                  <i className="bi bi-building"></i>
                  <span>{entidadSeleccionada.razonSocial || entidadSeleccionada.nombre}{entidadSeleccionada.cuit ? ` (${entidadSeleccionada.cuit})` : ''}</span>
                  <button className="buscador-seleccionado-cambiar" type="button">Cambiar</button>
                </div>
              ) : (
                <div className="buscador-input-wrap">
                  <i className="bi bi-search buscador-icon"></i>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar entidad por nombre o CUIT..."
                    value={entidadSearch}
                    autoFocus
                    onChange={(e) => { setEntidadSearch(e.target.value); setEntidadSearchOpen(true); setSelectedEntidadId(''); }}
                    onFocus={() => setEntidadSearchOpen(true)}
                    onBlur={() => setTimeout(() => setEntidadSearchOpen(false), 200)}
                  />
                  {entidadSearchOpen && (
                    <div className="buscador-dropdown">
                      {entidadesFiltradas.length === 0 ? (
                        <div className="buscador-no-results">Sin resultados</div>
                      ) : (
                        entidadesFiltradas.map(e => (
                          <div key={e.id} className="buscador-option" onClick={() => { setSelectedEntidadId(String(e.id)); setEntidadSearchOpen(false); setEntidadSearch(''); }}>
                            <span className="buscador-option-name">{e.razonSocial || e.nombre}</span>
                            <span className="buscador-option-meta">{e.cuit || ''}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="asignar-btn-wrap">
              <button className="btn btn-primary" onClick={handleAsignarTramite}>
                <i className="bi bi-play-fill"></i> Iniciar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderActivos() {
    if (entidadSeleccionada) {
      return renderTramitesEntidad();
    }
    return renderListaEntidades();
  }

  function renderListaEntidades() {
    return (
      <div className="admin-proc-section">
        <div className="section-header-custom">
          <h3>Trámites Activos por Entidad</h3>
          <span className="entidades-count">{entidades.length} entidades con trámites</span>
        </div>

        {loadingEntidades ? (
          <div className="admin-proc-empty"><p>Cargando entidades...</p></div>
        ) : entidades.length === 0 ? (
          <div className="admin-proc-empty"><p>No hay entidades con trámites activos.</p></div>
        ) : (
          <div className="entidades-list">
            {entidades.map((ent, index) => (
              <div key={ent.id} className="entidad-row" onClick={() => { setEntidadSeleccionada(ent.id); fetchTramitesEntidad(ent.id); }}>
                <span className="entidad-pos">#{index + 1}</span>
                <div className="entidad-info">
                  <span className="entidad-nombre">{ent.nombre}</span>
                  <span className="entidad-cuit">{ent.cuit || ''}</span>
                </div>
                <div className="entidad-tramites-count">
                  <span className="entidad-count-num">{ent.total_tramites}</span>
                  <span className="entidad-count-label">trámite{ent.total_tramites !== 1 ? 's' : ''}</span>
                </div>
                <i className="bi bi-chevron-right entidad-arrow"></i>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderTramitesEntidad() {
    if (loadingTramites) {
      return (
        <div className="admin-proc-section">
          <div className="admin-proc-empty"><p>Cargando trámites...</p></div>
        </div>
      );
    }

    return (
      <div className="admin-proc-section">
        <div className="section-header-custom">
          <div className="entidad-back-row">
            <button className="btn btn-outline-sm" onClick={() => { setEntidadSeleccionada(null); setTramitesEntidad([]); fetchEntidades(); }}>
              <i className="bi bi-arrow-left"></i> Volver
            </button>
            <h3 style={{ margin: 0 }}>
              {tramitesEntidad.length > 0 ? tramitesEntidad[0].entidad_nombre || 'Entidad' : 'Entidad'}
            </h3>
          </div>
        </div>

        {tramitesEntidad.length === 0 ? (
          <div className="admin-proc-empty"><p>Esta entidad no tiene trámites activos.</p></div>
        ) : (
          <div className="proc-activos-list">
            {tramitesEntidad.map(proc => {
              const progreso = calcularProgreso(proc.pasos);
              const completados = proc.pasos.filter(p => p.completado).length;
              const estaCompletado = proc.estado_general === 'Completado';

              return (
                <div key={proc.id} className={`proc-activo-card ${estaCompletado ? 'completado' : ''}`}>
                  <div className="proc-activo-header">
                    <div className="proc-activo-info">
                      <h4>{proc.plantilla_nombre}</h4>
                      <span className={`proc-estado ${estaCompletado ? 'estado-completado' : 'estado-pendiente'}`}>{proc.estado_general}</span>
                      {proc.iniciado_por === 'cliente' && <span className="badge-cliente"><i className="bi bi-person"></i> Iniciado por cliente</span>}
                    </div>
                    <div className="proc-activo-progreso">
                      <div className="proc-progreso-bar">
                        <div className="proc-progreso-fill" style={{ width: `${progreso}%` }}></div>
                      </div>
                      <span className="proc-progreso-text">{completados}/{proc.pasos.length}</span>
                    </div>
                  </div>

                  <p className="proc-activo-desc">{proc.plantilla_descripcion}</p>
                  <p className="proc-activo-fecha">
                    <i className="bi bi-building"></i> {proc.entidad_nombre} — Iniciado: {new Date(proc.iniciado_en).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>

                  <div className="proc-pasos-admin">
                    {proc.pasos.map((paso, index) => (
                      <div key={paso.id} className={`proc-paso ${paso.completado ? 'paso-completado' : ''}`}>
                        <div className="paso-numero">{index + 1}</div>
                        <div className="paso-contenido">
                          <div className="paso-header">
                            <h4>{paso.titulo_paso}</h4>
                            {paso.completado && (
                              <span className="paso-fecha-completado">
                                <i className="bi bi-check-circle-fill"></i>
                                {paso.fecha_completado ? new Date(paso.fecha_completado).toLocaleDateString('es-AR') : 'Completado'}
                              </span>
                            )}
                          </div>
                          <p>{paso.descripcion_paso}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="proc-activo-actions">
                    <button className="btn btn-outline" onClick={() => handleRetroceder(proc.id)} disabled={completados === 0}>
                      <i className="bi bi-arrow-counterclockwise"></i> Retroceder
                    </button>
                    <button className="btn btn-primary" onClick={() => handleAvanzar(proc.id)} disabled={estaCompletado}>
                      <i className="bi bi-check-lg"></i> Avanzar
                    </button>
                    <button className="btn btn-danger" onClick={() => handleEliminarSeguimiento(proc.id)} title="Eliminar trámite">
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderFinalizados() {
    return (
      <div className="admin-proc-section">
        <div className="section-header-custom">
          <h3>Trámites Finalizados</h3>
          {finalizados.length > 0 && (
            <span className="entidades-count">{finalizados.length} trámite{finalizados.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {loadingFinalizados ? (
          <div className="admin-proc-empty"><p>Cargando...</p></div>
        ) : finalizados.length === 0 ? (
          <div className="admin-proc-empty"><p>No hay trámites finalizados.</p></div>
        ) : (
          <div className="proc-activos-list">
            {finalizados.map(proc => {
              const completados = proc.pasos.filter(p => p.completado).length;

              return (
                <div key={proc.id} className="proc-activo-card completado">
                  <div className="proc-activo-header">
                    <div className="proc-activo-info">
                      <h4>{proc.plantilla_nombre}</h4>
                      <span className="proc-estado estado-completado">Completado</span>
                      {proc.iniciado_por === 'cliente' && <span className="badge-cliente"><i className="bi bi-person"></i> Iniciado por cliente</span>}
                    </div>
                    <div className="proc-activo-progreso">
                      <div className="proc-progreso-bar">
                        <div className="proc-progreso-fill" style={{ width: '100%', background: 'var(--success)' }}></div>
                      </div>
                      <span className="proc-progreso-text">{completados}/{proc.pasos.length}</span>
                    </div>
                  </div>

                  <p className="proc-activo-desc">{proc.plantilla_descripcion}</p>
                  <p className="proc-activo-fecha">
                    <i className="bi bi-building"></i> {proc.entidad_nombre} — Finalizado: {new Date(proc.iniciado_en).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>

                  <div className="proc-pasos-admin">
                    {proc.pasos.map((paso, index) => (
                      <div key={paso.id} className={`proc-paso ${paso.completado ? 'paso-completado' : ''}`}>
                        <div className="paso-numero">{index + 1}</div>
                        <div className="paso-contenido">
                          <div className="paso-header">
                            <h4>{paso.titulo_paso}</h4>
                            {paso.completado && (
                              <span className="paso-fecha-completado">
                                <i className="bi bi-check-circle-fill"></i>
                                {paso.fecha_completado ? new Date(paso.fecha_completado).toLocaleDateString('es-AR') : 'Completado'}
                              </span>
                            )}
                          </div>
                          <p>{paso.descripcion_paso}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="proc-activo-actions">
                    <button className="btn btn-danger" onClick={() => handleEliminarSeguimiento(proc.id)} title="Eliminar trámite">
                      <i className="bi bi-trash"></i> Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  function renderModalCrear() {
    return (
      <div className="modal-overlay" onClick={() => setShowCrearModal(false)}>
        <div className="modal-content-admin" onClick={e => e.stopPropagation()}>
          <h3>Crear Nueva Plantilla</h3>
          <form onSubmit={handleCrearPlantilla}>
            <div className="form-group-dash">
              <label>Nombre del trámite</label>
              <input type="text" className="form-control" value={formNombre} onChange={e => setFormNombre(e.target.value)} required />
            </div>
            <div className="form-group-dash">
              <label>Descripcion</label>
              <textarea className="form-control" rows="2" value={formDescripcion} onChange={e => setFormDescripcion(e.target.value)} required />
            </div>
            <div className="form-group-dash">
              <label>Categoría</label>
              <select className="form-control" value={formCategoriaId} onChange={e => setFormCategoriaId(e.target.value)}>
                <option value="">Sin categoría</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="form-section-divider"><h4>Pasos del Trámite</h4></div>
            {formPasos.map((paso, index) => (
              <div key={index} className="paso-form-row">
                <div className="paso-form-header">
                  <span className="paso-form-numero">Paso #{index + 1}</span>
                  {formPasos.length > 1 && (
                    <button type="button" className="btn-icon-danger-sm" onClick={() => handleQuitarPaso(index)}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
                <div className="form-group-dash">
                  <label>Titulo del paso</label>
                  <input type="text" className="form-control" value={paso.titulo_paso} onChange={e => handleCambiarPaso(index, 'titulo_paso', e.target.value)} required />
                </div>
                <div className="form-group-dash">
                  <label>Descripcion del paso</label>
                  <textarea className="form-control" rows="2" value={paso.descripcion_paso} onChange={e => handleCambiarPaso(index, 'descripcion_paso', e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline" onClick={handleAgregarPaso} style={{ width: '100%', marginBottom: '20px' }}>
              <i className="bi bi-plus-lg"></i> Agregar Paso
            </button>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowCrearModal(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary"><i className="bi bi-check-lg"></i> Crear Plantilla</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  function renderModalEditar() {
    return (
      <div className="modal-overlay" onClick={() => { setShowEditarModal(false); setEditarPlantillaId(null); }}>
        <div className="modal-content-admin" onClick={e => e.stopPropagation()}>
          <h3>Editar Plantilla</h3>

          <div className="alert-warning"  style={{marginBottom: "15px"}}>
            <i className="bi bi-info-circle-fill"></i>
            <span>Editar esta plantilla <strong>no modifica los pasos</strong> de los trámites que ya fueron iniciados. Solo el titulo y la descripcion se veran reflejados. Los cambios aplican solo a nuevas asignaciones.</span>
          </div>

          <form onSubmit={handleEditarPlantilla}>
            <div className="campo-reflejado">
              <label>
                Nombre del trámite
                <span className="campo-badge"><i className="bi bi-arrow-repeat"></i> Se refleja</span>
              </label>
              <input type="text" className="form-control" value={formNombre} onChange={e => setFormNombre(e.target.value)} required />
            </div>
            <div className="campo-reflejado">
              <label>
                Descripcion
                <span className="campo-badge"><i className="bi bi-arrow-repeat"></i> Se refleja</span>
              </label>
              <textarea className="form-control" rows="2" value={formDescripcion} onChange={e => setFormDescripcion(e.target.value)} required />
            </div>
            <div className="form-group-dash">
              <label>Categoría</label>
              <select className="form-control" value={formCategoriaId} onChange={e => setFormCategoriaId(e.target.value)}>
                <option value="">Sin categoría</option>
                {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="form-section-divider"><h4>Pasos del Trámite</h4></div>
            {formPasos.map((paso, index) => (
              <div key={index} className="paso-form-row">
                <div className="paso-form-header">
                  <span className="paso-form-numero">Paso #{index + 1}</span>
                  {formPasos.length > 1 && (
                    <button type="button" className="btn-icon-danger-sm" onClick={() => handleQuitarPaso(index)}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
                <div className="form-group-dash">
                  <label>Titulo del paso</label>
                  <input type="text" className="form-control" value={paso.titulo_paso} onChange={e => handleCambiarPaso(index, 'titulo_paso', e.target.value)} required />
                </div>
                <div className="form-group-dash">
                  <label>Descripcion del paso</label>
                  <textarea className="form-control" rows="2" value={paso.descripcion_paso} onChange={e => handleCambiarPaso(index, 'descripcion_paso', e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline" onClick={handleAgregarPaso} style={{ width: '100%', marginBottom: '20px' }}>
              <i className="bi bi-plus-lg"></i> Agregar Paso
            </button>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => { setShowEditarModal(false); setEditarPlantillaId(null); }}>Cancelar</button>
              <button type="submit" className="btn btn-primary"><i className="bi bi-check-lg"></i> Guardar Cambios</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  function renderModalCategoria() {
    return (
      <div className="modal-overlay" onClick={() => setShowCategoriaModal(false)}>
        <div className="modal-content-admin" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
          <h3><i className="bi bi-folder-plus" style={{ marginRight: 8 }}></i>Nueva Categoría</h3>
          <form onSubmit={handleCrearCategoria}>
            <div className="form-group-dash">
              <label>Nombre de la categoría</label>
              <input type="text" className="form-control" value={catModalNombre} onChange={e => setCatModalNombre(e.target.value)} placeholder="Ej: IMPOSITIVOS" autoFocus required />
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button type="button" className="btn btn-outline" onClick={() => setShowCategoriaModal(false)}>Cancelar</button>
              <button type="submit" className="btn btn-primary"><i className="bi bi-check-lg"></i> Crear</button>
            </div>
          </form>
        </div>
      </div>
    );
  }
};

export default AdminTramites;
